import React from "react";
import {Button, ButtonBase} from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import EditIcon from "@material-ui/icons/Edit";
import LabelOutlinedIcon from "@material-ui/icons/LabelOutlined";
import {ClientContext, ClientContextId} from "common/types/client_context";

export type ContextControlVariant = "row" | "chip";

// The one green used wherever context is shown as set, shared so the edit
// controls and the read-only add-in button cannot drift apart.
export const contextSetPalette = {
  borderColor: "#b7dfba",
  backgroundColor: "#f6fbf6",
  color: "#2e7d32",
};

interface ContextVariantProps {
  label: string | null;
  onClick: () => void;
  style?: React.CSSProperties;
}

interface ContextControlProps extends ContextVariantProps {
  variant?: ContextControlVariant;
}

export function contextSummaryLabel(
  context: string,
  clientContextId: ClientContextId | null,
  presets: ClientContext[],
): string | null {
  const hasText = (context ?? "").trim() !== "";
  if (!hasText && !clientContextId) {
    return null;
  }
  if (!clientContextId) {
    return "Custom context";
  }
  const preset = presets.find(item => item.id === clientContextId);
  return preset ? preset.name : "Client context";
}

const ellipsis: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ContextRow: React.FC<ContextVariantProps> = ({label, onClick, style}) => {
  const isSet = Boolean(label);
  return (
    <ButtonBase
      onClick={onClick}
      style={{
        width: "100%",
        display: "block",
        textAlign: "left",
        padding: "6px 8px",
        borderRadius: "4px",
        border: `1px solid ${
          isSet ? contextSetPalette.borderColor : "#dcdcdc"
        }`,
        backgroundColor: isSet ? contextSetPalette.backgroundColor : "#fff",
        ...style,
      }}
    >
      <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
        <LabelOutlinedIcon
          style={{
            fontSize: "1.125rem",
            flexShrink: 0,
            color: isSet ? contextSetPalette.color : "#9e9e9e",
          }}
        />
        <div style={{flex: 1, minWidth: 0}}>
          <div
            style={{
              fontSize: "0.625rem",
              lineHeight: "14px",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#757575",
            }}
          >
            Client context
          </div>
          <div
            title={label ?? undefined}
            style={{
              fontSize: "0.8125rem",
              lineHeight: "18px",
              fontWeight: isSet ? 600 : 400,
              color: isSet ? contextSetPalette.color : "#9e9e9e",
              ...ellipsis,
            }}
          >
            {label ?? "None"}
          </div>
        </div>
        <ChevronRightIcon
          style={{fontSize: "1.125rem", flexShrink: 0, color: "#9e9e9e"}}
        />
      </div>
    </ButtonBase>
  );
};

const ContextChip: React.FC<ContextVariantProps> = ({
  label,
  onClick,
  style,
}) => {
  const isSet = Boolean(label);
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      startIcon={<LabelOutlinedIcon style={{fontSize: "1rem"}} />}
      endIcon={isSet ? <EditIcon style={{fontSize: "0.875rem"}} /> : undefined}
      title={label ?? undefined}
      style={{
        maxWidth: "16em",
        textTransform: "none",
        ...(isSet ? {...contextSetPalette, fontWeight: 600} : {}),
        ...style,
      }}
    >
      <span style={ellipsis}>{label ?? "Client Context"}</span>
    </Button>
  );
};

const ContextControl: React.FC<ContextControlProps> = ({
  label,
  variant = "chip",
  onClick,
  style,
}) => {
  if (variant === "row") {
    return <ContextRow label={label} onClick={onClick} style={style} />;
  }
  return <ContextChip label={label} onClick={onClick} style={style} />;
};

export default ContextControl;
